import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const EditJobCoachAssignmentForm = ({ assignment, onBack, onSuccess }) => {
  const { user: currentUser } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    consumer_id: assignment.consumer_id || "",
    user_id: assignment.user_id || "",
    start_date: assignment.start_date || "",
    end_date: assignment.end_date || "",
  });

  const [consumers, setConsumers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updated_by = currentUser?.user_id;
  const tenant_id = assignment?.tenant_id;
  const tenant_type = currentUser?.tenant_type;
  const role = currentUser?.role;

  useEffect(() => {
    if (!tenant_id || !updated_by || !tenant_type || !role) {
      return;
    }

    const controller = new AbortController();

    const fetchConsumers = async () => {
      try {
        const res = await secureAxios.get("/api/consumers", {
          params: {
            tenant_id,
            user_id: updated_by,
            role,
            tenant_type,
          },
          signal: controller.signal,
        });
        setConsumers(res.data);
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error("Error fetching consumers:", err.message);
        }
      }
    };

    const fetchUsers = async () => {
      try {
        const res = await secureAxios.get("/api/users", {
          params: {
            tenant_id,
            role: "job_coach",
          },
          signal: controller.signal,
        });
        setUsers(res.data);
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error("Error fetching users:", err.message);
        }
      }
    };

    fetchConsumers();
    fetchUsers();

    return () => controller.abort();
  }, [tenant_id, updated_by, role, tenant_type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toISOString().split("T")[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!updated_by) {
      alert("Missing user info. Please log in again.");
      return;
    }

    if (formData.end_date && formData.start_date > formData.end_date) {
      alert("End date cannot be before start date");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        updated_by,
        updated_by_name: currentUser?.name,
        updated_by_role: currentUser?.role,
        original_consumer_id: assignment.consumer_id,
        original_user_id: assignment.user_id,
        original_start_date: assignment.start_date,
        original_end_date: assignment.end_date,
      };

      await secureAxios.put(
        `/api/job-coach-assignments/${assignment.assignment_id}`,
        payload
      );

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error updating assignment:", err.message);
      const errorMessage =
        err.response?.data?.error ||
        "An error occurred while updating the assignment.";
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label htmlFor="edit-consumer-select" className="form-label">
          Consumer <span className="text-danger">*</span>
        </label>
        <select
          id="edit-consumer-select"
          className="form-control"
          name="consumer_id"
          value={formData.consumer_id}
          onChange={handleChange}
          required
          disabled={isSubmitting}
        >
          <option value="">-- Select Consumer --</option>
          {consumers.map((c) => (
            <option key={c.consumer_id} value={c.consumer_id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label htmlFor="edit-coach-select" className="form-label">
          Job Coach <span className="text-danger">*</span>
        </label>
        <select
          id="edit-coach-select"
          className="form-control"
          name="user_id"
          value={formData.user_id}
          onChange={handleChange}
          required
          disabled={isSubmitting}
        >
          <option value="">-- Select Job Coach --</option>
          {users.map((u) => (
            <option key={u.user_id} value={u.user_id}>
              {u.name} ({u.email})
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label htmlFor="edit-start-date" className="form-label">
          Start Date <span className="text-danger">*</span>
        </label>
        <input
          id="edit-start-date"
          type="date"
          className="form-control"
          name="start_date"
          value={formatDateForInput(formData.start_date)}
          onChange={handleChange}
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="edit-end-date" className="form-label">
          End Date
        </label>
        <input
          id="edit-end-date"
          type="date"
          className="form-control"
          name="end_date"
          value={formatDateForInput(formData.end_date)}
          onChange={handleChange}
          disabled={isSubmitting}
        />
        <small className="form-text text-muted">
          Leave blank if assignment is still active
        </small>
      </div>

      <div className="d-flex gap-2">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Updating..." : "Update Assignment"}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default EditJobCoachAssignmentForm;
